import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

const ACTION_META = {
  SEND_ORDER_CONFIRMATION: {
    title: "Skicka orderbekräftelse",
    icon: FactCheckOutlinedIcon,
  },
  SEND_DELIVERY_NOTICE: {
    title: "Skicka leveransbekräftelse",
    icon: LocalShippingOutlinedIcon,
  },
};

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const ActionConfirmDialog = ({
  open,
  order,
  actions = [],
  onClose,
  onConfirm,
}) => {
  const safeActions = Array.isArray(actions) ? actions : [];
  const multiple = safeActions.length > 1;

  const initialState = useMemo(() => {
    return safeActions.map((type) => ({
      type,
      selected: true,
      emails:
        order?.email ? [order.email] : [],
      input: "",
      error: "",
    }));
  }, [safeActions, order]);

  const [actionState, setActionState] = useState(initialState);

  useEffect(() => {
    if (open) setActionState(initialState);
  }, [open, initialState]);

  const updateAction = (type, updater) => {
    setActionState((prev) =>
      prev.map((a) =>
        a.type === type
          ? typeof updater === "function"
            ? updater(a)
            : { ...a, ...updater }
          : a
      )
    );
  };

  const addEmails = (type, raw) => {
    if (!raw) return;

    const candidates = raw
      .split(/[;,]/)
      .map((e) => e.trim())
      .filter(Boolean);

    updateAction(type, (a) => {
      const newEmails = [...a.emails];
      let error = "";

      candidates.forEach((email) => {
        if (!isValidEmail(email)) {
          error = `Ogiltig e-post: ${email}`;
        } else if (!newEmails.includes(email)) {
          newEmails.push(email);
        }
      });

      return { ...a, emails: newEmails, input: "", error };
    });
  };

  const removeEmail = (type, email) => {
    updateAction(type, (a) => ({
      ...a,
      emails: a.emails.filter((e) => e !== email),
    }));
  };

  const selectedActions = actionState.filter((a) => a.selected);

  // 🚨 VALIDATION
  const hasInvalidSelectedAction = selectedActions.some(
    (a) => a.emails.length === 0
  );

  const handleSubmit = () => {
    if (hasInvalidSelectedAction) return;

    const payload = selectedActions.map((a) => ({
      type: a.type,
      recipients: a.emails,
    }));

    onConfirm?.(payload);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
          {multiple
            ? "Skicka bekräftelser?"
            : `${ACTION_META[safeActions[0]]?.title ?? "Skicka"} nu?`}
        </Typography>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 13, mb: 2, color: "text.secondary" }}>
          Ordern är sparad. Välj vilka bekräftelser som ska skickas.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          {actionState.map((action) => {
            const meta = ACTION_META[action.type];
            const Icon = meta.icon;

            const isInvalid =
              action.selected && action.emails.length === 0;

            return (
              <Box key={action.type}>
                <Stack direction="row" spacing={1.5}>
                  {multiple && (
                    <Checkbox
                      checked={action.selected}
                      onChange={() =>
                        updateAction(action.type, {
                          selected: !action.selected,
                        })
                      }
                    />
                  )}

                  <Icon
                    fontSize="small"
                    sx={{
                      mt: 0.5,
                      color: isInvalid
                        ? "error.main"
                        : "text.secondary",
                    }}
                  />

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {meta.title}
                    </Typography>

                    <Box
                      sx={{
                        mt: 0.25,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                      }}
                    >
                      {action.emails.map((email) => (
                        <Chip
                          key={email}
                          label={email}
                          size="small"
                          onDelete={() =>
                            removeEmail(action.type, email)
                          }
                          sx={{
                            height: 22,
                            fontSize: 12,
                          }}
                        />
                      ))}

                      <TextField
                        variant="standard"
                        placeholder="Lägg till e-post…"
                        value={action.input}
                        error={isInvalid || !!action.error}
                        helperText={
                          isInvalid
                            ? "Minst en e-postadress krävs."
                            : action.error
                        }
                        onChange={(e) =>
                          updateAction(action.type, {
                            input: e.target.value,
                            error: "",
                          })
                        }
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" ||
                            e.key === "," ||
                            e.key === ";"
                          ) {
                            e.preventDefault();
                            addEmails(
                              action.type,
                              action.input
                            );
                          }
                        }}
                        onBlur={() =>
                          addEmails(action.type, action.input)
                        }
                        sx={{
                          minWidth: 160,
                          "& .MuiInputBase-root": {
                            marginTop: 0,
                          },
                          "& .MuiInputBase-input": {
                            padding: 0,
                            fontSize: 13,
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "rgba(0,0,0,0.15)",
                          },
                          "& .MuiInput-underline:hover:before": {
                            borderBottomColor: "rgba(0,0,0,0.25)",
                          },
                          "& .MuiInput-underline:after": {
                            borderBottomColor: "primary.main",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        <Typography
          sx={{
            fontSize: 12,
            color: "text.secondary",
            mt: 3,
            opacity: 0.7,
          }}
        >
          Du kan alltid skicka dessa senare från ordern.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Inte nu</Button>

        <Button
          variant="contained"
          disabled={
            selectedActions.length === 0 ||
            hasInvalidSelectedAction
          }
          onClick={handleSubmit}
        >
          {selectedActions.length === 1
            ? ACTION_META[selectedActions[0]?.type]?.title
            : "Skicka"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ActionConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
  actions: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ActionConfirmDialog;